import React, { useState, useRef } from 'react';

import {
    Chip,
    Popper,
    Fade,
    Paper,
    ClickAwayListener,
    MenuList,
    MenuItem,
    IconButton,
    ListSubheader,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { makeStyles } from '@material-ui/core/styles';
import { MTableFilterInput } from './MTableFilterInput';
import { MTableSearch } from './MTableSearch';
import FabDropdownMenu from '../FabDropdownMenu';

function addOrChangeFilter(columns, filters, label, type, value) {
    const column = columns.find(column => column.field === type);
    const index = filters.findIndex(item => item && item.type === type);

    if (index > -1 && !column.multipleFilter) {
        filters[index].value = value;
    } else {
        filters.push({ type, value, label });
    }

    return filters;
}

const useStyles = makeStyles(theme => ({
    menuItem: {
        display: 'flex',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    popper: {
        zIndex: 1300,
    },
    textField: {
        paddingTop: 0,
        marginTop: 0,
        marginBottom: 0,
    },
    card: {
        minWidth: 275,
    },
    cardContent: {
        display: 'flex',
        flexDirection: 'column-reverse',
    },
    addButton: {
        marginRight: theme.spacing(2),
        zIndex: 100,
        display: 'inline-flex',
        verticalAlign: 'middle',
    },
    actions: {
        float: 'right',
    },
    clickable: {
        color: theme.palette.text.secondary,
        ...theme.typography,
        cursor: 'pointer',
        marginLeft: theme.spacing(2),
    },
    chip: {
        margin: `${theme.spacing()}px ${theme.spacing()}px ${theme.spacing()}px 0`,
    },
}));

const containInFilters = (column, filters) =>
    filters.find(filter => column.field === filter.type);

function getColumnsForFiltering(columns, filters) {
    return columns.filter(column => {
        const { hidden = false, notFilter = false } = column;
        return (
            !hidden &&
            !notFilter &&
            (!containInFilters(column, filters) || column.multipleFilter)
        );
    });
}

const MTableTitle = ({
    primaryFilters = [],
    filtersValues = [],
    onFiltersChange,
    onSearchSubmit,
    useCustomSearch,
    searchValue,
    addMenuItems,
    canChange = true,
    data = [],
    columns,
    title,
}) => {
    const classes = useStyles();
    const [filters, setFilters] = useState([...filtersValues]);
    const [openSelect, setOpenSelect] = useState(false);
    const [openFilterInput, setOpenFilterInput] = useState(null);
    const [filterValue, setFilterValue] = useState('');
    const anchorRef = useRef(null);

    const onAddFilter = (label, type, val) => {
        const updatedFilters = addOrChangeFilter(
            columns,
            filters,
            label,
            type,
            val
        );
        setFilters(updatedFilters);
        if (onFiltersChange) {
            onFiltersChange(updatedFilters);
        }
    };

    const handleToggle = () => setOpenSelect(prevOpen => !prevOpen);

    const handleClose = event => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpenSelect(false);
        setFilterValue('');
        setOpenFilterInput(null);
    };

    const selectFilter = filter => {
        setOpenSelect(false);
        setOpenFilterInput(filter);
    };

    const filterValueChange = value => {
        setFilterValue(value);
    };

    const applyFilter = () => {
        onAddFilter(openFilterInput.title, openFilterInput.field, filterValue);
        setOpenFilterInput(null);
        setFilterValue('');
    };

    const deleteFilter = filter => {
        const updatedFilters = filters.filter(
            item =>
                item.type !== filter.type ||
                (item.type === filter.type && item.value !== filter.value)
        );
        setFilters(updatedFilters);
        onFiltersChange(updatedFilters);
    };

    const filterOptions = getColumnsForFiltering(columns, filters);

    return (
        <div>
            {canChange && (
                <div className={classes.addButton}>
                    <FabDropdownMenu menuItems={addMenuItems} />
                </div>
            )}
            {title}
            <IconButton variant="contained" onClick={handleToggle}>
                <FilterListIcon />
            </IconButton>
            {primaryFilters.length > 0 &&
                primaryFilters.map(filter => filter({ onChange: onAddFilter }))}
            {filters.map((filter, i) => (
                <Chip
                    key={`${filter.type}_${i}`}
                    label={`${filter.label} - ${filter.value}`}
                    onDelete={() => deleteFilter(filter)}
                    className={classes.chip}
                />
            ))}
            <span
                ref={anchorRef}
                className={classes.clickable}
                onClick={handleToggle}
            >
                Add filter
            </span>
            {useCustomSearch && (
                <MTableSearch
                    searchValue={searchValue}
                    onSearchSubmit={onSearchSubmit}
                />
            )}
            <Popper
                open={openSelect}
                anchorEl={anchorRef.current}
                transition
                className={classes.popper}
                placement="bottom-end"
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    subheader={
                                        <ListSubheader component="div">
                                            Filter by
                                        </ListSubheader>
                                    }
                                >
                                    {filterOptions.map(column => (
                                        <MenuItem
                                            key={column.field}
                                            onClick={() => selectFilter(column)}
                                        >
                                            {column.title}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Fade>
                )}
            </Popper>
            <Popper
                open={Boolean(openFilterInput)}
                anchorEl={anchorRef.current}
                transition
                className={classes.popper}
                placement="bottom-end"
            >
                <Fade>
                    <>
                        <MTableFilterInput
                            filter={openFilterInput}
                            onClose={handleClose}
                            onChange={filterValueChange}
                            onSubmit={applyFilter}
                            value={filterValue}
                            data={data}
                        />
                    </>
                </Fade>
            </Popper>
        </div>
    );
};

export { MTableTitle };
